/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { IModelInputFactory } from "./IModelInputFactory";
import { IModelOutputFactory } from "./IModelOutputFactory";
import { EventEmitter } from "../EventEmitter";
import { IModelData } from "./IModelData";

export interface IModelHandlerInputFactories {
	[K: string]: IModelInputFactory<any, any, any>;
}

export type TModelHandlerInputMap<T extends IModelHandlerInputFactories = {}> = {
	[P in keyof T]?: ReturnType<T[P]>;
};

export interface IModelHandlerOutputFactories {
	[K: string]: IModelOutputFactory<any, any>;
}

export type TModelHandlerOutputMap<T extends IModelHandlerOutputFactories = {}> = {
	[P in keyof T]?: ReturnType<T[P]>;
};

/**
 * Model handler
 */
export class ModelHandler<
	InputsType extends IModelHandlerInputFactories = {},
	OutputsType extends IModelHandlerOutputFactories = {}
> extends EventEmitter<"update"> {

	/** Model inputs */
	public inputs: TModelHandlerInputMap<InputsType> = {};

	/** Model outputs */
	public outputs: TModelHandlerOutputMap<OutputsType> = {};

	public constructor(
		inputs: InputsType,
		outputs: OutputsType
	) {

		super();

		for (const k in inputs) {
			this.inputs[k] = inputs[k](this) as any;
		}

		for (const k in outputs) {
			this.outputs[k] = outputs[k](this) as any;
		}

	}

	/**
	 * Reset all inputs to initia values
	 */
	public reset() {

		for (const k in this.inputs) {
			this.inputs[k].reset(true, false);
		}

		this.update();

	}

	/**
	 * Updates the model
	 *
	 * Is called automatically by InputFields when new value is set
	 */
	public update() {

		for (const k in this.outputs) {
			this.outputs[k].update();
		}

		this.emit("update");

	}

	/**
	 * Returns model data
	 */
	public getData(): IModelData {

		const data: IModelData = {
			inputs: {},
			outputs: {}
		};

		for (const k in this.inputs) {
			data.inputs[k] = this.inputs[k].getInputValue();
		}

		for (const k in this.outputs) {
			data.outputs[k] = this.outputs[k].get();
		}

		return data;

	}

	/**
	 * Set values from data
	 * @param data Model data
	 */
	public setData(data: IModelData) {

		for (const k in this.inputs) {
			if (data.inputs[k] !== undefined) {
				this.inputs[k].set(data.inputs[k], true, false);
			}
		}

		this.update();

	}

}
